<template>
  <div id="app">
    <template v-if="authenticated">
      <el-container direction="vertical" class="h-full">
        <header-nav />
        <el-container class="main-wrapper h-[calc(100%-72px)]">
          <sidebar />
          <el-main v-loading="isLoading && !showLoadingUnifiedCommerce">
            <router-view v-if="!isLoading" />
          </el-main>
        </el-container>
      </el-container>
      <mobile-view
        v-if="
          !showLoadingUnifiedCommerce &&
          isWhiteLabel &&
          $route.path !== '/home' &&
          !mobileModalDismissed
        "
        @modal-dismissed="handleMobileModalDismissed"
      />
      <session-expired-modal />
    </template>
    <template v-else>
      <router-view />
    </template>
    <div id="th-modals" />
    <div v-if="showLoadingUnifiedCommerce" id="th-loading">
      <div id="th-spinner" />
    </div>
  </div>
</template>

<script>
import get from 'just-safe-get'
import { isUnzerSdkLoaded } from '@shared/utils/unzer-sdk'
import HeaderNav from './components/nav/header.vue'
import Sidebar from './components/sidebar/index.vue'
import MobileView from './../apps/unzer/views/mobile-view/mobile-view.vue'
import SessionExpiredModal from '@shared/components/session-expired-modal'
import { isUnifiedCommerce, isKeycloackEnabled } from '@shared/constants'
import { mapState, mapActions, storeToRefs } from 'pinia'
import * as Sentry from '@sentry/vue'
import {
  hidden,
  visibilityChange,
  languageISO,
  gastroLocaleSuffix
} from './constants'
import { useWhitelabelThemeStore } from './store/whitelabel-theme.ts'
import { useMessagesStore } from './store/messages'
import { useExportsStore } from '@shared/store/exports'
import { useConfigurationStore } from '@shared/store/configuration'
import { useAuthStore } from '@shared/store/auth'
import AuthService from '@shared/plugins/auth'

export default {
  name: 'App',
  components: {
    HeaderNav,
    Sidebar,
    MobileView,
    SessionExpiredModal
  },
  setup() {
    const themeStore = useWhitelabelThemeStore()
    const { themeClass } = storeToRefs(themeStore)
    return { themeStore, themeClass }
  },
  data: () => {
    return {
      isLoading: true,
      mobileModalDismissed: false,
      lastTokenRefresh: 0,
      tokenRefreshThrottleMs: 20000, // 20 seconds throttle
      activityListeners: [] // Store listener references for cleanup
    }
  },
  computed: {
    ...mapState(useAuthStore, ['isGastro', 'user', 'authenticated']),
    ...mapState(useConfigurationStore, ['locale']),
    isWhiteLabel() {
      return isUnifiedCommerce()
    },
    showLoadingUnifiedCommerce() {
      return this.isWhiteLabel && this.isLoading
    }
  },
  watch: {
    $route() {
      // Recheck modal dismissed state when route changes
      this.checkMobileModalDismissed()
    },
    locale() {
      this.maybeChangeLocale()
    },
    themeClass: {
      handler() {
        document.documentElement.className = ''
        document.documentElement.classList.add(this.themeClass)
      },
      immediate: true
    }
  },
  async created() {
    this.isLoading = true

    // Check if mobile modal was dismissed
    this.checkMobileModalDismissed()

    await useAuthStore().waitThInitializated()
    const messagesStore = useMessagesStore()
    messagesStore.clearLocalOperations()
    messagesStore.clearStaleMessages()
    useExportsStore().restorePendingExports()
    this.themeStore.fetchDNSConfig()
    await Promise.all([this.loadTenantConfig(), useAuthStore().waitForMeData()])
    this.isLoading = false

    this.maybeChangeLocale()

    document.addEventListener(
      visibilityChange,
      (v) => this.handleVisibilityChange(v),
      false
    )

    // Add user activity listeners for token refresh
    if (isUnifiedCommerce() && isKeycloackEnabled()) {
      this.setupUserActivityListeners()
    }
  },
  async mounted() {
    window.scrollTo(0, 1)
    await this.adjustMetaTags()
    this.checkMobileModalDismissed()
  },
  beforeUnmount() {
    // Clean up event listeners to prevent memory leaks
    if (this.activityListeners.length > 0) {
      this.cleanupUserActivityListeners()
    }
  },
  methods: {
    ...mapActions(useConfigurationStore, ['loadTenantConfig']),
    checkMobileModalDismissed() {
      // Check localStorage for dismissed state
      this.mobileModalDismissed =
        localStorage.getItem('unzer_mobile_modal_dismissed') === 'true'
    },
    handleMobileModalDismissed() {
      // Called when the mobile modal is dismissed
      this.mobileModalDismissed = true
      localStorage.setItem('unzer_mobile_modal_dismissed', 'true')
    },
    maybeChangeLocale(locale = this.locale) {
      let newLocale = String(locale)
      const l = newLocale || this.$i18n.locale
      if (
        this.isGastro &&
        (l === languageISO.ENGLISH || l === languageISO.GERMAN)
      ) {
        newLocale = l + gastroLocaleSuffix
      }

      if (!newLocale) return // Skip no language

      const isSupported = this.$i18n.availableLocales.includes(newLocale)
      if (!isSupported) return

      // document.documentElement.lang has no other source of truth —
      // index.html ships a static "en" — and the Unzer SDK reads only this
      // attribute, never $i18n.locale, so it must stay in sync even when
      // $i18n.locale already happens to match newLocale (e.g. vue-i18n's
      // navigator.language guess coincides with the persisted choice).
      // Track whether it actually changed so the reload below only fires
      // when there's something new for the SDK to pick up.
      const documentLangChanged = document.documentElement.lang !== newLocale
      document.documentElement.lang = newLocale

      if (this.$i18n.locale !== newLocale) {
        this.$i18n.locale = newLocale
        this.$log.debug('Language:', this.$i18n.locale)
        Sentry.getCurrentScope().setTag('page_locale', this.$i18n.locale)
      }

      if (!documentLangChanged) return

      // Unzer's web-component widgets read document.documentElement.lang
      // exactly once, when their SDK module first evaluates — confirmed via
      // the SDK's own source, which throws if its localization setup is
      // invoked twice. Only a page reload can make an already-loaded SDK
      // pick up a new language. But `locale` also blends in tenantConfig
      // (account-language and other settings-page fields), which stays an
      // unsaved draft until the page's Save button succeeds — see
      // settings-toolbar.vue's tenantConfigIsDirty gate. Reloading on a mere
      // draft selection would discard it before Save is clicked, so only
      // reload for the already-persisted localConfig path; a tenantConfig
      // draft is handled after a successful save, in
      // shared/views/settings/sections/dashboard/index.vue's handleSave().
      const isPersistedImmediately =
        get(useConfigurationStore().localConfig, 'settings.language') ===
        newLocale

      if (isPersistedImmediately && isUnzerSdkLoaded()) {
        window.location.reload()
      }
    },
    handleVisibilityChange() {
      if (document[hidden] === false) {
        this.makeMeCall()
        // Refresh Keycloak token if it's Unzer
        if (isUnifiedCommerce() && isKeycloackEnabled()) {
          AuthService.refreshKeycloakToken()
        }
      }
    },
    async makeMeCall() {
      return useAuthStore().getMeData()
    },
    setupUserActivityListeners() {
      // List of user activity events to listen for
      const activityEvents = [
        'mousedown',
        'mousemove',
        'keypress',
        'scroll',
        'touchstart',
        'click'
      ]

      // Throttled token refresh function
      const throttledTokenRefresh = () => {
        const now = Date.now()
        if (now - this.lastTokenRefresh > this.tokenRefreshThrottleMs) {
          this.lastTokenRefresh = now
          AuthService.refreshKeycloakToken()
        }
      }

      // Add event listeners for user activity
      activityEvents.forEach((event) => {
        document.addEventListener(event, throttledTokenRefresh)

        // Store listener reference for cleanup
        this.activityListeners.push({
          event,
          handler: throttledTokenRefresh
        })
      })
    },
    cleanupUserActivityListeners() {
      // Remove all activity listeners
      this.activityListeners.forEach(({ event, handler }) => {
        document.removeEventListener(event, handler)
      })
      // Clear the listeners array
      this.activityListeners = []
    },
    async adjustMetaTags() {
      const name = this.isWhiteLabel ? 'Unzer Dashboard' : 'Tillhub Dashboard'
      const iconsLink = this.isWhiteLabel
        ? 'public'
        : 'https://storage.googleapis.com/tillhub-api-images/dashboard/logos'

      const iconSizes = [
        '57x57',
        '60x60',
        '72x72',
        '76x76',
        '114x114',
        '120x120',
        '144x144',
        '152x152',
        '180x180'
      ]

      const faviconSizes = ['16x16', '32x32', '96x96']
      const descriptionName = 'Management Dashboard for the ' + name + ' POS'
      const AndroidRequiredIcon = this.isWhiteLabel
        ? await import(
            '@shared/assets/icons/meta-icons/android-icon-192x192.png'
          ).then((result) => result.default)
        : iconsLink + '/icons/android-icon-192x192.png'

      const url = this.isWhiteLabel
        ? 'https://dashboard.unzer.com'
        : 'https://dashboard.tillhub.com'

      document.head.insertAdjacentHTML(
        'beforeEnd',
        '<meta name="description" content="' +
          name +
          ' - The management platform for your POS system." />'
      )
      document.head.insertAdjacentHTML(
        'beforeEnd',
        '<meta property="og:title" content="' + name + '" />'
      )
      document.head.insertAdjacentHTML(
        'beforeEnd',
        '<meta property="og:description" content="' + descriptionName + '" />'
      )
      document.head.insertAdjacentHTML(
        'beforeEnd',
        '<meta property="og:url" content="' + url + '" />'
      )
      document.head.insertAdjacentHTML(
        'beforeEnd',
        '<meta property="twitter:title" content="' + name + '" />'
      )
      document.head.insertAdjacentHTML(
        'beforeEnd',
        '<meta property="twitter:description" content="' +
          descriptionName +
          '" />'
      )

      for (const size of iconSizes) {
        const requiredIcon = this.isWhiteLabel
          ? await import(
              `@shared/assets/icons/meta-icons/apple-icon-${size}.png`
            ).then((result) => result.default)
          : iconsLink + '/icons/apple-icon-' + size + '.png'

        document.head.insertAdjacentHTML(
          'beforeEnd',
          '<link rel="apple-touch-icon" type="image/png" sizes="' +
            size +
            '" href="' +
            requiredIcon +
            '" />'
        )
      }

      for (const size of faviconSizes) {
        const requiredIcon = this.isWhiteLabel
          ? await import(
              `@shared/assets/icons/meta-icons/favicon-${size}.png`
            ).then((result) => result.default)
          : iconsLink + '/icons/favicon-' + size + '.png'
        document.head.insertAdjacentHTML(
          'beforeEnd',
          '<link rel="icon" type="image/png" sizes="' +
            size +
            '" href="' +
            requiredIcon +
            '" />'
        )
      }

      document.head.insertAdjacentHTML(
        'beforeEnd',
        '<meta rel="icon" type="image/png" sizes="192x192" href="' +
          AndroidRequiredIcon +
          '" />'
      )
      document.head.insertAdjacentHTML(
        'beforeEnd',
        '<meta name="msapplication-TileImage" content="' +
          iconsLink +
          '/icons/ms-icon-144x144.png" />'
      )

      if (!this.isWhiteLabel) {
        const favicon = document.getElementById('favicon')
        favicon.href =
          'https://storage.googleapis.com/tillhub-api-images/dashboard/logos/icons/favicon.ico'
      }
    }
  }
}
</script>
